<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  inboxName: {
    type: String,
    default: '',
  },
  inboxIcon: {
    type: String,
    default: '',
  },
  metrics: {
    type: Object,
    default: () => ({
      recipients_count: 0,
      sent_count: 0,
      failed_count: 0,
      delivered_count: 0,
      read_count: 0,
    }),
  },
});

const { t } = useI18n();

const hasMetrics = computed(
  () => props.metrics !== null && props.metrics !== undefined
);

const deliveryRate = computed(() => {
  if (!props.metrics.recipients_count) return 0;
  return Math.round(
    (props.metrics.delivered_count / props.metrics.recipients_count) * 100
  );
});

const readRate = computed(() => {
  if (!props.metrics.recipients_count) return 0;
  return Math.round(
    (props.metrics.read_count / props.metrics.recipients_count) * 100
  );
});
</script>

<template>
  <div class="flex items-center gap-3 flex-wrap">
    <div class="flex items-center gap-1.5">
      <Icon :icon="inboxIcon" class="text-n-slate-11" size="16" />
      <span class="text-xs text-n-slate-11">{{ inboxName }}</span>
    </div>

    <template v-if="hasMetrics">
      <div class="flex items-center gap-1.5">
        <Icon icon="i-lucide-users" class="text-n-slate-11" size="14" />
        <span class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.METRICS.RECIPIENTS') }}: {{ metrics.recipients_count }}
        </span>
      </div>

      <div class="flex items-center gap-1.5">
        <Icon icon="i-lucide-send" class="text-n-teal-11" size="14" />
        <span class="text-xs text-n-teal-11">
          {{ t('CAMPAIGN.METRICS.SENT') }}: {{ metrics.sent_count }}
        </span>
        <template v-if="metrics.failed_count > 0">
          <span class="text-xs text-n-slate-11">/</span>
          <Icon icon="i-lucide-x-circle" class="text-n-red-11" size="14" />
          <span class="text-xs text-n-red-11">
            {{ t('CAMPAIGN.METRICS.FAILED') }}: {{ metrics.failed_count }}
          </span>
        </template>
      </div>

      <div class="flex items-center gap-1.5">
        <Icon icon="i-lucide-check-check" class="text-n-blue-11" size="14" />
        <span class="text-xs text-n-blue-11">
          {{ t('CAMPAIGN.METRICS.DELIVERED') }}: {{ metrics.delivered_count }}
          <template v-if="metrics.delivered_count > 0">
            ({{ deliveryRate }}%)
          </template>
        </span>
      </div>

      <div class="flex items-center gap-1.5">
        <Icon icon="i-lucide-eye" class="text-n-violet-11" size="14" />
        <span class="text-xs text-n-violet-11">
          {{ t('CAMPAIGN.METRICS.READ') }}: {{ metrics.read_count }}
          <template v-if="metrics.read_count > 0"> ({{ readRate }}%) </template>
        </span>
      </div>
    </template>
  </div>
</template>
